import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuthor } from '@/hooks/useAuthor';
import { useNWC } from '@/hooks/useNWCContext';
import { useStreamingPreferences } from '@/hooks/useStreamingPreferences';
import type { MusicTrack } from '@/hooks/useMusicTracks';

// Get LNURL pay endpoint from lightning address
async function getLnurlPayEndpoint(lnAddress: string): Promise<{ callback: string; minSendable: number; maxSendable: number } | null> {
  try {
    const [name, domain] = lnAddress.split('@');
    if (!name || !domain) return null;
    
    const url = `https://${domain}/.well-known/lnurlp/${name}`;
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.callback && data.minSendable !== undefined && data.maxSendable !== undefined) {
      return {
        callback: data.callback,
        minSendable: data.minSendable,
        maxSendable: data.maxSendable,
      };
    }
    return null;
  } catch (error) {
    console.error('Failed to get LNURL endpoint:', error);
    return null;
  }
}

interface StreamingState {
  totalSatsSent: number;
  isStreaming: boolean;
}

export function useStreamingPayments(currentTrack: MusicTrack | null, isPlaying: boolean) {
  const { connections, getActiveConnection, sendPayment } = useNWC();
  const { preferences, getTotalPerMinute, getArtistAmount } = useStreamingPreferences();
  
  const author = useAuthor(currentTrack?.event.pubkey);
  const metadata = author.data?.metadata;
  
  const [state, setState] = useState<StreamingState>({
    totalSatsSent: 0,
    isStreaming: false,
  });
  
  const intervalRef = useRef<number | null>(null);
  const isPayingRef = useRef(false);
  const lastTrackIdRef = useRef<string | null>(null);
  const hasStartedRef = useRef(false);
  
  // Store all values needed for payment in refs
  const paymentDataRef = useRef({
    currentTrack,
    metadata,
    getArtistAmount,
    getActiveConnection,
    sendPayment,
  });
  
  // Update the ref whenever values change (no dependency array = runs every render)
  useEffect(() => {
    paymentDataRef.current = {
      currentTrack,
      metadata,
      getArtistAmount,
      getActiveConnection,
      sendPayment,
    };
  });
  
  // Calculate conditions
  const hasLnAddress = !!(metadata?.lud16 || metadata?.lud06);
  const hasWallet = connections.length > 0 && connections.some(c => c.isConnected);
  const artistAmount = getArtistAmount();
  
  // Create a stable key for the streaming session
  const streamingKey = useMemo(() => {
    const canStream = preferences.enabled && artistAmount > 0 && hasWallet && hasLnAddress && isPlaying && !!currentTrack;
    if (!canStream) return null;
    // Return a stable key only when we CAN stream
    return `${currentTrack?.id}-${isPlaying}`;
  }, [preferences.enabled, artistAmount > 0, hasWallet, hasLnAddress, isPlaying, currentTrack?.id]);
  
  const shouldStream = streamingKey !== null;
  
  // Reset total when track changes
  useEffect(() => {
    if (currentTrack?.id !== lastTrackIdRef.current) {
      lastTrackIdRef.current = currentTrack?.id || null;
      setState({ totalSatsSent: 0, isStreaming: shouldStream });
      hasStartedRef.current = false;
    }
  }, [currentTrack?.id, shouldStream]);
  
  // Update streaming state
  useEffect(() => {
    setState(prev => ({ ...prev, isStreaming: shouldStream }));
  }, [shouldStream]);
  
  // Set up streaming interval
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (!streamingKey) {
      hasStartedRef.current = false;
      return;
    }
    
    // Prevent duplicate starts for same session
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    
    console.log('🎵 Starting streaming payments - will send every 60 seconds');
    
    // The payment function - uses plain LNURL payments (no zap events)
    const doPayment = async () => {
      if (isPayingRef.current) {
        console.log('Payment already in progress, skipping');
        return;
      }
      
      const data = paymentDataRef.current;
      
      if (!data.currentTrack || !data.metadata) {
        console.log('Streaming payment skipped: missing data');
        return;
      }
      
      const lnAddress = data.metadata.lud16 || data.metadata.lud06;
      if (!lnAddress) {
        console.log('Streaming payment skipped: no lightning address');
        return;
      }
      
      const amount = data.getArtistAmount();
      if (amount <= 0) {
        console.log('Streaming payment skipped: amount is 0');
        return;
      }
      
      isPayingRef.current = true;
      console.log(`⚡ Sending streaming payment: ${amount} sats to ${data.currentTrack.artist}`);
      
      try {
        // Get LNURL pay endpoint from lightning address
        const lnurlData = await getLnurlPayEndpoint(lnAddress);
        if (!lnurlData) {
          console.warn('Failed to get LNURL endpoint for artist');
          return;
        }
        
        const amountMsats = amount * 1000;
        
        // Check if amount is within allowed range
        if (amountMsats < lnurlData.minSendable || amountMsats > lnurlData.maxSendable) {
          console.warn(`Amount ${amountMsats} msats outside allowed range [${lnurlData.minSendable}, ${lnurlData.maxSendable}]`);
          return;
        }
        
        // Request invoice from LNURL callback (no zap request - just plain payment)
        const separator = lnurlData.callback.includes('?') ? '&' : '?';
        const res = await fetch(`${lnurlData.callback}${separator}amount=${amountMsats}`);
        const responseData = await res.json();
        
        if (!res.ok || !responseData.pr) {
          console.warn('Failed to get invoice:', responseData);
          return;
        }
        
        const connection = data.getActiveConnection();
        if (!connection?.isConnected) {
          console.warn('No active wallet connection');
          return;
        }
        
        await data.sendPayment(connection, responseData.pr);
        
        console.log(`✅ Streaming payment sent: ${amount} sats to ${data.currentTrack.artist}`);
        
        setState(prev => ({
          ...prev,
          totalSatsSent: prev.totalSatsSent + amount,
        }));
        
      } catch (error) {
        console.error('Streaming payment failed:', error);
      } finally {
        isPayingRef.current = false;
      }
    };
    
    // Set interval
    intervalRef.current = window.setInterval(() => {
      console.log('⏰ 60 second interval triggered');
      doPayment();
    }, 60000);
    
    return () => {
      if (intervalRef.current !== null) {
        console.log('🛑 Stopping streaming payments');
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      hasStartedRef.current = false;
    };
  }, [streamingKey]);
  
  return {
    totalSatsSent: state.totalSatsSent,
    isStreaming: state.isStreaming,
    satsPerMinute: getTotalPerMinute(),
    artistSatsPerMinute: artistAmount,
  };
}
